import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import "core-js/modules/es6.array.slice.js";
import { useEffect, useRef, useState, useMemo } from 'react'; // Leaving the `isMobile` reference in here on purpose, commented out
// For why, see the comments surrounding `setSelectionRange` further down
// import isMobile from 'ismobilejs';

import { createPatternFormatter } from './pattern-formatter'; // This hook returns the parts needed to control an input or textarea
// while physically redacting or masking parts of the field as a user types
//
// props:
//   formatPattern: the pattern to control the formatting of what a user has typed
//                   see pattern-formatter.js for examples and descriptions
//
//   initialValue: initial value to be rendered in input field.  Will be formatted upon creation
//
//   onChange: handler to call when the input field's value has changed
//             pass the user's supplied `onChange` to the hook, and pass the handler returned from this hook to the child component this hook controls
//
//
// returns: [formatter, inputRef, onChange]
//
// formatter: the instance of the state machine keeping track of input's
//             formatted and raw values for usage in rendering.
//
// inputRef: the useRef() ref for the input/textarea to be controlled
//           this ref MUST be passed into the child component being controlled
//
// onChange: the onChange handler to update the state-machine
//           this handler wraps the user's `onChange` handler supplied to the hook
//           this handler MUST be passed into the child component being controlled
//
// Example:
//
// const TelephoneInput = ({ defaultValue, onChange }) => {
//   const [
//     formatter,
//     inputRef,
//     onChangeControlled
//   ] = usePatternFormatter({
//     formatPattern: '+1 (999) 999 - 9999',
//     initialValue: defaultValue,
//     onChange,
//   });
//
//   return (
//     <input
//       ref={inputRef}
//       onChange={onChangeControlled}
//       value={formatter.value}
//     />
//   );
// };
//

export function usePatternFormatter(_ref) {
  var formatPattern = _ref.formatPattern,
      initialValue = _ref.initialValue,
      _ref$onChange = _ref.onChange,
      emitChange = _ref$onChange === void 0 ? function () {} : _ref$onChange;
  // create a ref to pass into the input/text area
  // to get change events for updating the formatter state
  var inputRef = useRef(null);
  var formatterRef = useRef(null);
  var prevValueRef = useRef(null);

  var _useState = useState(0),
      _useState2 = _slicedToArray(_useState, 2),
      formatted = _useState2[0],
      setFormatted = _useState2[1]; // initialize the formatter state machine


  useMemo(function () {
    var nextFormatter = createPatternFormatter(formatPattern);

    if (formatterRef.current) {
      // update new formatter with current value
      nextFormatter.insert(formatterRef.current.raw);
    } else {
      // fill with initial value
      nextFormatter.insert(initialValue);
      prevValueRef.current = nextFormatter.value;
    } // replace the ref


    formatterRef.current = nextFormatter; // trigger an update

    var value = nextFormatter.value,
        selection = nextFormatter.selection;
    setFormatted({
      value: value,
      selection: selection
    });
  }, // eslint-disable-next-line react-hooks/exhaustive-deps -- directive added automatically by Shepherd migration
  [formatPattern]); // now that state machine is initialized
  // grab a prettier reference to it

  var formatter = formatterRef.current; // useEffect to listen to focus and blur events on inputEl for tracking caret selection

  useEffect(function () {
    var _inputRef$current2, _inputRef$current3;

    // updates the selection in this component's formatter state
    // this method is bound to document 'selectionchange'
    // on focus and unbound on blur
    var updateCurrentSelectionFromRef = function updateCurrentSelectionFromRef() {
      // find the input element and read its selection range
      var _inputRef$current = inputRef.current,
          selectionStart = _inputRef$current.selectionStart,
          selectionEnd = _inputRef$current.selectionEnd;
      var prevSelection = formatter.selection;

      if (selectionStart !== prevSelection.selectionStart || selectionEnd !== prevSelection.selectionEnd) {
        formatter.setSelection({
          selectionStart: selectionStart,
          selectionEnd: selectionEnd
        });
      }
    };

    var startlisteningForSelectionChange = function startlisteningForSelectionChange() {
      document.addEventListener('selectionchange', updateCurrentSelectionFromRef); // firefox does not fire the 'selectionchange' event on document
      // for selection changes coming from within an input
      // so instead attach to mouseup and keyup events.

      window.addEventListener('mouseup', updateCurrentSelectionFromRef);
      window.addEventListener('keyup', updateCurrentSelectionFromRef); // and listen to 'select' events to pick up selections
      // that did not originate from mouse of keyboard interaction
      // i.e. "Select All" from the "Edit" menu

      inputRef.current.addEventListener('select', updateCurrentSelectionFromRef);
    };

    var stoplisteningForSelectionChange = function stoplisteningForSelectionChange() {
      document.removeEventListener('selectionchange', updateCurrentSelectionFromRef);
      window.removeEventListener('mouseup', updateCurrentSelectionFromRef);
      window.removeEventListener('keyup', updateCurrentSelectionFromRef);
      inputRef.current.removeEventListener('select', updateCurrentSelectionFromRef);
    };

    (_inputRef$current2 = inputRef.current) === null || _inputRef$current2 === void 0 ? void 0 : _inputRef$current2.addEventListener('focus', startlisteningForSelectionChange);
    (_inputRef$current3 = inputRef.current) === null || _inputRef$current3 === void 0 ? void 0 : _inputRef$current3.addEventListener('blur', stoplisteningForSelectionChange);
    return function () {
      stoplisteningForSelectionChange();
      inputRef.current.removeEventListener('focus', startlisteningForSelectionChange); // eslint-disable-next-line react-hooks/exhaustive-deps -- directive added automatically by Shepherd migration

      inputRef.current.removeEventListener('blur', stoplisteningForSelectionChange);
    };
  }, // eslint-disable-next-line react-hooks/exhaustive-deps -- directive added automatically by Shepherd migration
  []);

  var onChange = function onChange(e) {
    // determine which chars have changed
    // formatter holds previous selection state
    var chars = e.target.value.slice(formatter.selection.selectionStart, e.target.selectionEnd); // stash the value to determine if change should be emitted

    prevValueRef.current = formatter.value; // update the formatter state

    var _formatter$selection = formatter.selection,
        selectionStart = _formatter$selection.selectionStart,
        selectionEnd = _formatter$selection.selectionEnd; // inputType is a new(-er)(-ish) part of the InputEvent spec
    // inspecting it can give more information about what action the user performed
    // In this handler, it is only inspecting it to handle one specific case,
    // but more conditions/cases could allow for supporting undo/redo and other behaviors
    // InputEvent 2020 draft spec here: https://w3c.github.io/input-events/#dom-inputevent-inputtype

    var inputType = e.nativeEvent.inputType;

    if (chars) {
      // user inserted new chars
      formatter.insert(chars);
    } else {
      // user pressed the backspace key
      // eslint-disable-next-line no-lonely-if
      if (selectionStart !== selectionEnd) {
        // user pressed backspace with a selection
        formatter.backspace();
      } else if ( // chrome, firefox
      inputType === 'deleteSoftLineBackward' || // safari
      inputType === 'deleteHardLineBackward') {
        // user held meta key and pressed backspace with nothing selected
        formatter.deleteLineBackward();
      } else {
        // user pressed backspace with nothing selected
        formatter.backspace();
      }
    } // trigger an update


    var value = formatter.value,
        selection = formatter.selection;
    setFormatted({
      value: value,
      selection: selection
    });
  };

  useEffect(function () {
    var setSelectionRange = function setSelectionRange() {
      var domNode = inputRef.current; // read selection from state captures on last change

      var _formatted$selection = formatted.selection,
          selectionStart = _formatted$selection.selectionStart,
          selectionEnd = _formatted$selection.selectionEnd; // Prevents iOS bug where setting selection range on an inactive element completely breaks the page

      if (domNode === document.activeElement) {
        // update the user's selection in the input
        // since masking characters are placed in the input
        // after the end of the current formatted value,
        // ensure the caret does not extend past the end of the
        // formatted value (where the user should be typing)
        domNode.setSelectionRange(Math.min(selectionStart, formatted.value.length), Math.min(selectionEnd, formatted.value.length), 'none');
      }

      if (formatter.value !== prevValueRef.current) {
        // fire event handlers after selectionRange has been properly set
        emitChange({
          target: {
            value: formatter.value
          }
        });
      }
    };

    setSelectionRange(); // There used to be special handling logic for this code running on android.
    // It was added to work around tricky bugs in various android keyboards when
    // reporting selection ranges that existed in 2017.
    // The solution in the past was to defer the setting of selectionRange
    // until next-tick.  It was a head-scratcher and that was the best-available
    // fix that dsturley and benjroy could come up with at the time.
    // It is 2020 now, and most of the environments seem to not experience the
    // bugs anymore. Leaving the original implementation commented out here for
    // archeological purposes.
    //
    // if (isMobile.android.device) {
    //   setTimeout(setSelectionRange, 0);
    // } else {
    //   setSelectionRange();
    // }
    //
    // some interesting discussion from 2017 that got us started down that path
    // of defering the selection on android:
    // https://github.com/text-mask/text-mask/issues/300#issuecomment-282293346
    //
    // following up on that led to some very interesting discussion in draft-js
    // tangentially related to the issue we face here in trying to control inputs
    // https://github.com/facebook/draft-js/pull/2035
    // https://github.com/facebook/draft-js/pull/2035/files?diff=split&w=1#r266204210
    //
    // And that PR links to PRs in slate editor, also interesting
    // They handle the different android APIs very robustly in that PR,
    // and the write-ups in the diff describe the behavioral differences in incredible detail
    // and explains the edge cases that could exist that this code doesn't necessarily handle
    // https://github.com/ianstormtaylor/slate/pull/2565
  }, // eslint-disable-next-line react-hooks/exhaustive-deps -- directive added automatically by Shepherd migration
  [formatted]);
  return [formatter, inputRef, onChange];
}